import { hasStylesToRemove, IEditorV3, removeStylesFromEditorV3 } from "@azu-business/editor-v3";
import { AioExternalReplacements } from "aic";
import { AioExternalSingleWithSource, eSingleSource } from "contexts/hierarchy/interface";
import { eTlfItemType } from "functions/api/enums";
import { TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { STYLES_TO_CLEAN, STYLES_TO_DELETE } from "../../../config";
import { removeStylesFromBlockLine } from "./removeStylesFromBlockLine";
import { removeStylesFromSingles } from "./removeStylesFromSingles";
import { removeStylesFromTableData } from "./removeStylesFromTableData";

/**
 * Removes Optional (green) and Notes (blue) styles from TLF content including titles,
 * footnotes, page by, body (tables/listings only), and singles. Also promotes external
 * singles that contain styles to TLF-level singles by changing their source.
 *
 * This is a pure transformation function that does not dispatch updates to context.
 * The caller is responsible for dispatching the returned partial TLF data.
 *
 * @param tlfData - The TLF item to process
 * @param externalSinglesForTiFo - External singles available to the TLF (from hierarchy/templates)
 * @param externalLists - External lists available to the TLF (from hierarchy/templates)
 * @returns Partial TLF data with styles removed, ready to be dispatched to TLF context
 */
export const removeStylesFromTlf = (
  tlfData: TlfItemReadDto,
  externalSinglesForTiFo: AioExternalSingleWithSource<IEditorV3>[],
  externalLists: AioExternalReplacements<IEditorV3>[],
): Partial<TlfItemReadDto> => {
  const newTitles = tlfData.titles.map((line) => removeStylesFromBlockLine(line));
  const newFootnotes = tlfData.footnotes.map((line) => removeStylesFromBlockLine(line));
  const newPageBy = {
    ...tlfData.pageBy,
    content: removeStylesFromEditorV3(tlfData.pageBy.content, STYLES_TO_DELETE, STYLES_TO_CLEAN),
  };

  // Only tables/listings have AitTableData bodies with styled text
  const newBody =
    tlfData.type === eTlfItemType.table || tlfData.type === eTlfItemType.listing
      ? removeStylesFromTableData(tlfData.body, externalLists)
      : undefined;

  // Promote external singles with styles to TLF-level singles, but only if their oldText is not already present in TLF-level singles (after style removal)
  const externalSinglesWithStylesToPromote = externalSinglesForTiFo
    .filter(
      (single) =>
        single.source !== eSingleSource.tlfItem &&
        hasStylesToRemove(single.newText, [...STYLES_TO_DELETE, ...STYLES_TO_CLEAN]) &&
        !tlfData.singles.some((tlfSingle) => tlfSingle.oldText === single.oldText),
    )
    .map((single) => ({
      ...single,
      source: eSingleSource.tlfItem,
    }));

  const newSingles = removeStylesFromSingles([
    ...tlfData.singles,
    ...externalSinglesWithStylesToPromote,
  ]);

  return {
    titles: newTitles,
    footnotes: newFootnotes,
    pageBy: newPageBy,
    ...(newBody !== undefined && { body: newBody }),
    singles: newSingles,
  };
};
